import { useEffect, useMemo, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { cn } from '@/lib/cn';
import { apiErrorMessage } from '@shared/api/client';
import { uploadDocument } from '@shared/api/documents';
import {
  deriveMessageStatus,
  useChatHistory,
  useChatSocket,
  useMarkRead,
  useSendMessage,
  useThreadReceipts,
} from '@shared/hooks/use-chat';
import type { ChatMessage, ReadReceipt, Uuid } from '@shared/types/api';
import { MessageBubble } from './message-bubble';
import { Composer } from './composer';
import { useAdminTransaction, useReviewCancellationRequest } from '../../../hooks/use-admin';
import { Button } from '@/components/ui/button';

function sameDay(a: string, b: string) {
  return new Date(a).toDateString() === new Date(b).toDateString();
}
function dayLabel(iso: string) {
  const today = new Date();
  const y = new Date();
  y.setDate(today.getDate() - 1);
  if (sameDay(iso, today.toISOString())) return 'Today';
  if (sameDay(iso, y.toISOString())) return 'Yesterday';
  return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Admin chat thread. Visual parity with the client's ChatThread minus the
 * features admin doesn't need:
 *   - No reply / pin / star / react / search bar — those are conversation
 *     tools for the long-running participants, not the operator.
 *   - No typing indicator out (admin typing status is noise to the
 *     participants) but we still consume incoming typing events so the
 *     admin sees when the client or associate is typing.
 *   - Supports attachment upload (matching client chatroom attachment options).
 * Everything else — day separators, sender grouping, avatars, tails,
 * hover cards, auto-scroll, read receipts — mirrors the client thread.
 */
export function AdminChatThread({
  transactionId,
  myUserId,
  readOnly = false,
}: {
  transactionId: Uuid;
  myUserId: Uuid | undefined;
  readOnly?: boolean;
}) {
  const { data: tx } = useAdminTransaction(transactionId);
  const reviewCancel = useReviewCancellationRequest();
  const history = useChatHistory(transactionId);
  const { typingUserIds } = useChatSocket(transactionId);
  const send = useSendMessage(transactionId);
  const markRead = useMarkRead(transactionId);
  const { data: threadReceipts } = useThreadReceipts(transactionId);
  const peerReceipts = threadReceipts?.receipts ?? [];

  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const prevNewestRef = useRef<string | null>(null);

  // Server returns newest-first; reverse to render oldest→newest.
  const messages = useMemo(() => {
    const items = history.data?.items ?? [];
    const reversed = [...items].reverse();

    let latestProposedId: string | null = null;
    let latestFinalizedId: string | null = null;

    for (let i = reversed.length - 1; i >= 0; i--) {
      const m = reversed[i];
      const kind = m.metadata?.kind as string | undefined;
      const isScopeForm =
        m.type === 'SCOPE_FORM' ||
        (kind?.startsWith('scope_form') && kind !== 'scope_form:accepted');

      if (isScopeForm) {
        const isProposed = kind === 'scope_form:proposed' || m.metadata?.status === 'PROPOSED';
        if (isProposed) {
          if (!latestProposedId) latestProposedId = m.id;
        } else {
          if (!latestFinalizedId) latestFinalizedId = m.id;
        }
      }
    }

    if (!latestProposedId && !latestFinalizedId) return reversed;

    return reversed.filter((m) => {
      const kind = m.metadata?.kind as string | undefined;
      const isScopeForm =
        m.type === 'SCOPE_FORM' ||
        (kind?.startsWith('scope_form') && kind !== 'scope_form:accepted');

      if (isScopeForm) {
        const isProposed = kind === 'scope_form:proposed' || m.metadata?.status === 'PROPOSED';
        if (isProposed && latestProposedId && m.id !== latestProposedId) {
          return false;
        }
        if (!isProposed && latestFinalizedId && m.id !== latestFinalizedId) {
          return false;
        }
      }
      return true;
    });
  }, [history.data?.items]);

  useEffect(() => {
    const newestId = messages[messages.length - 1]?.id ?? null;
    if (!newestId) return;
    if (prevNewestRef.current !== newestId) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
      prevNewestRef.current = newestId;
      markRead.mutate(newestId);
    }
  }, [messages, markRead]);

  const upload = useMutation({
    mutationFn: (file: File) => uploadDocument(transactionId, file, 'SUPPORTING'),
  });

  function handleSend(body: string, file: File | null, extraMetadata?: Record<string, unknown>) {
    if (file) {
      upload.mutate(file, {
        onSuccess: (doc) => {
          send.mutate({
            body: body || undefined,
            attachmentDocumentId: doc.id,
            metadata: {
              fileName: doc.fileName,
              mimeType: doc.mimeType,
              ...(extraMetadata || {}),
            },
          });
        },
      });
    } else {
      send.mutate({ body, metadata: extraMetadata });
    }
  }

  // Filter out the admin's own id from "typing" indicator — don't announce
  // that the admin is typing back to themselves.
  const othersTyping = [...typingUserIds].filter((id) => id !== myUserId);

  return (
    <section
      className="flex min-h-0 flex-1 min-w-0 flex-col relative overflow-hidden"
      style={{
        backgroundColor: '#efeae2',
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60' viewBox='0 0 60 60'%3E%3Cpath d='M9 24c0-2.2 1.8-4 4-4s4 1.8 4 4-1.8 4-4 4-4-1.8-4-4zm30 18c0-2.2 1.8-4 4-4s4 1.8 4 4-1.8 4-4 4-4-1.8-4-4zM12 36c0-1.1.9-2 2-2s2 .9 2 2-.9 2-2 2-2-.9-2-2zm28-24c0-1.1.9-2 2-2s2 .9 2 2-.9 2-2 2-2-.9-2-2zm-16 10c0-1.7 1.3-3 3-3s3 1.3 3 3-1.3 3-3 3-3-1.3-3-3zm18 18c0-1.7 1.3-3 3-3s3 1.3 3 3-1.3 3-3 3-3-1.3-3-3z' fill='%23e5ddd5' fill-opacity='0.45' fill-rule='evenodd'/%3E%3C/svg%3E")`,
      }}
    >
      <div className="flex flex-wrap items-center justify-between border-b border-gray-200 bg-white/95 backdrop-blur-xs px-5 py-2 text-[11px] text-gray-500 gap-2 shadow-2xs z-10">
        <div className="flex items-center gap-2">
          <span className="italic">
            You are posting as <span className="font-semibold text-slate-900">Admin</span>
          </span>
        </div>
      </div>

      {/* ── Client Grievance / Cancellation Request Pending Banner ───────── */}
      {tx?.cancellationRequestStatus === 'PENDING' ? (
        <div className="border-b border-amber-300 bg-amber-50 p-3 px-5 text-xs text-amber-900 flex flex-wrap items-center justify-between gap-3 shadow-xs">
          <div className="flex-1 min-w-[240px]">
            <div className="flex items-center gap-2">
              <span className="font-bold bg-amber-200 text-amber-900 border border-amber-300 px-2 py-0.5 rounded text-[10px] uppercase">
                Client Grievance Pending
              </span>
              <span className="font-semibold text-amber-950">Cancellation Request Raised</span>
            </div>
            <p className="mt-1 text-amber-900 font-medium">
              Client Reason: <span className="italic text-amber-950">"{tx.cancellationRequestReason || 'No reason provided'}"</span>
            </p>
          </div>
          {!readOnly ? (
            <div className="flex items-center gap-2 shrink-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => reviewCancel.mutate({ id: transactionId, action: 'REJECT' })}
                disabled={reviewCancel.isPending}
                className="h-7 text-xs border-amber-300 text-amber-900 hover:bg-amber-100"
              >
                Reject Request
              </Button>
              <Button
                type="button"
                onClick={() => reviewCancel.mutate({ id: transactionId, action: 'ACCEPT' })}
                disabled={reviewCancel.isPending}
                className="h-7 text-xs bg-red-600 hover:bg-red-700 text-white font-semibold shadow-xs"
              >
                {reviewCancel.isPending ? 'Processing…' : 'Accept Cancel Request'}
              </Button>
            </div>
          ) : (
            <span className="text-xs text-amber-800 font-medium bg-amber-100 px-2.5 py-1 rounded">View Only</span>
          )}
        </div>
      ) : null}

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4">
        {history.isLoading ? (
          <p className="text-center text-sm text-gray-500">Loading messages…</p>
        ) : messages.length === 0 ? (
          <p className="mt-6 text-center text-sm text-gray-500">No messages yet.</p>
        ) : (
          <MessageList messages={messages} myId={myUserId} peerReceipts={peerReceipts} />
        )}
        {othersTyping.length > 0 ? (
          <div className="mt-3 text-xs italic text-gray-500">Someone is typing…</div>
        ) : null}
        {upload.isError ? (
          <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            Upload failed: {apiErrorMessage(upload.error, (upload.error as Error)?.message)}
          </div>
        ) : null}
        {upload.isPending ? (
          <div className="mt-3 rounded-md border border-gray-200 bg-white px-3 py-2 text-xs text-gray-600">
            Uploading attachment…
          </div>
        ) : null}
        <div ref={bottomRef} />
      </div>

      {readOnly ? (
        <div className="border-t border-gray-200 bg-white px-5 py-3 text-center text-xs text-gray-500 italic">
          View only mode — you do not have permission to post messages in this transaction.
        </div>
      ) : (
        <Composer
          disabled={send.isPending || upload.isPending || tx?.status === 'COMPLETE' || tx?.status === 'CANCELLED'}
          onSend={handleSend}
        />
      )}
    </section>
  );
}


function MessageList({
  messages,
  myId,
  peerReceipts,
}: {
  messages: ChatMessage[];
  myId: Uuid | undefined;
  peerReceipts: ReadReceipt[];
}) {
  const rows: React.ReactNode[] = [];
  let lastDate = '';
  let lastSender: string | null | undefined = undefined;

  for (const m of messages) {
    // Day separator — pill in the centre whenever the calendar day changes.
    if (!lastDate || !sameDay(m.createdAt, lastDate)) {
      rows.push(
        <div key={`d-${m.id}`} className="my-3 flex items-center justify-center">
          <span className="rounded-full bg-white/90 backdrop-blur-xs border border-gray-200/60 px-3 py-1 text-[11px] font-medium text-gray-600 shadow-2xs">
            {dayLabel(m.createdAt)}
          </span>
        </div>,
      );
      lastDate = m.createdAt;
      lastSender = undefined;
    }
    const isMine = !!myId && m.senderUserId === myId;
    const showSender = m.senderUserId !== lastSender;
    rows.push(
      <div
        key={m.id}
        // Clear break between speakers; tighter stack for grouped runs.
        className={cn(showSender ? 'mt-3' : 'mt-0.5')}
      >
        <MessageBubble
          message={m}
          isMine={isMine}
          showSender={showSender}
          deliveryStatus={isMine ? deriveMessageStatus(m, peerReceipts, myId) : null}
        />
      </div>,
    );
    lastSender = m.senderUserId;
  }

  return <div className="space-y-1">{rows}</div>;
}
